import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, StyleProp, ViewStyle } from 'react-native';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { GlassCard } from '../GlassCard';

interface IconStatCardProps {
  label: string;
  caption: string;
  iconBg: string;
  Icon: React.ComponentType<{ width?: number; height?: number; color?: string }>;
  iconColor: string;
  onPress: () => void;
  /** Override individual item wrapper style (e.g. fixed width on mobile) */
  itemStyle?: StyleProp<ViewStyle>;
  /**
   * When true, a leading numeric count in the caption (e.g. "3 open") is
   * emphasized: a corner badge on the icon plus the number rendered bold in the
   * card's accent color. No badge shows when the count is 0. Used by Quick
   * Actions to surface new/unread/open counts.
   */
  showCountBadge?: boolean;
}

/**
 * Vertical icon-stat card used by home-screen grids (Quick Actions,
 * My Community). Stacks icon → label → caption so the text gets the
 * full card width and never fights the icon for horizontal space.
 */
export const IconStatCard: React.FC<IconStatCardProps> = React.memo(({
  label,
  caption,
  iconBg,
  Icon,
  iconColor,
  onPress,
  itemStyle,
  showCountBadge = false,
}) => {
  const { colors } = useThemeColors();

  // Pull a leading integer out of the caption ("3 open" → 3) so the badge and
  // the bold caption number always agree — both come from the same string.
  const countMatch = showCountBadge ? caption.match(/^(\d+)\b/) : null;
  const count = countMatch ? parseInt(countMatch[1], 10) : 0;
  const hasCount = count > 0;

  return (
    <TouchableOpacity
      style={[styles.itemWrapper, itemStyle]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <GlassCard
        borderRadius={REDESIGN_SPACING.quickActionRadius}
        padding={19}
        style={styles.card}
      >
        <View style={styles.iconWrap}>
          <View style={[styles.icon, { backgroundColor: iconBg }]}>
            <Icon
              width={REDESIGN_SPACING.iconSize.resourceIconInner}
              height={REDESIGN_SPACING.iconSize.resourceIconInner}
              color={iconColor}
            />
          </View>
          {hasCount && (
            <View style={[styles.badge, { backgroundColor: colors.accent.red }]}>
              <Text style={styles.badgeText} numberOfLines={1}>
                {count > 99 ? '99+' : count}
              </Text>
            </View>
          )}
        </View>
        <Text style={[styles.label, { color: colors.text.tertiary }]} numberOfLines={1}>
          {label}
        </Text>
        {hasCount ? (
          <Text style={[styles.caption, { color: colors.text.dim }]} numberOfLines={2}>
            <Text style={[styles.captionCount, { color: iconColor }]}>{countMatch![1]}</Text>
            {caption.slice(countMatch![1].length)}
          </Text>
        ) : (
          <Text style={[styles.caption, { color: colors.text.dim }]} numberOfLines={2}>
            {caption}
          </Text>
        )}
      </GlassCard>
    </TouchableOpacity>
  );
});

IconStatCard.displayName = 'IconStatCard';

const styles = StyleSheet.create({
  itemWrapper: {
    flexBasis: '48%',
    flexGrow: 1,
  },
  card: {
    gap: 12,
    // Reserves enough vertical space for icon + label + a 2-line caption +
    // padding, so every card in the grid lines up regardless of caption
    // length or row position. Without this, rows whose captions wrap to two
    // lines stretch taller than rows whose captions fit on one.
    minHeight: 155,
  },
  iconWrap: {
    position: 'relative',
    alignSelf: 'flex-start',
  },
  icon: {
    width: REDESIGN_SPACING.iconSize.resourceIcon,
    height: REDESIGN_SPACING.iconSize.resourceIcon,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badge: {
    position: 'absolute',
    top: -6,
    right: -6,
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    paddingHorizontal: 5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeText: {
    color: PRIMITIVE_COLORS.white,
    fontSize: 11,
    fontWeight: '700',
    lineHeight: 14,
  },
  label: {
    ...REDESIGN_TYPOGRAPHY.cardTitle,
  },
  caption: {
    ...REDESIGN_TYPOGRAPHY.caption,
    lineHeight: 16.8,
  },
  captionCount: {
    fontWeight: '700',
  },
});
